import { makeObservable, observable, runInAction } from 'mobx'

import { Clock, IdGenerator, Logger } from '../ports'
import {
  Attachment,
  Chat,
  Contact,
  FailedMessage,
  SendingMessage,
} from '../protocol/types'
import { describeChatError, fold } from '../result'
import { ChatApi } from '../transport/api'

export type DraftMessage = SendingMessage | FailedMessage

// A conversation that does not exist on the server yet: a recipient plus the
// messages composed before the channel is created. The first send triggers a
// single-flight create; sends that land while it is in flight only queue.
// Once the channel exists the session takes over (materialize) and this
// store is done — a Chat's id is never mutated in place.
export class DraftLogStore {
  public messages: DraftMessage[] = []
  public materializedChat: Chat = null

  private isCreating = false
  private isHandedOff = false

  public constructor(
    public readonly recipient: Contact,
    private readonly userId: string,
    private readonly api: ChatApi,
    private readonly clock: Clock,
    private readonly newId: IdGenerator,
    private readonly logger: Logger,
    private readonly onChannelCreated: (
      channelId: string,
      pending: readonly DraftMessage[],
    ) => void,
  ) {
    makeObservable(this, {
      messages: observable.ref,
      materializedChat: observable.ref,
    })
  }

  public sendMessage(
    text: string,
    attachments: readonly Attachment[] = [],
  ): void {
    const trimmed = text.trim()
    if ((!trimmed && !attachments.length) || this.isHandedOff) {
      return
    }

    const sending: SendingMessage = {
      state: 'sending',
      fakeId: this.newId(),
      channelId: '',
      authorId: this.userId,
      createdAt: this.clock(),
      text: trimmed,
      attachments,
    }
    runInAction(() => {
      this.messages = [...this.messages, sending]
    })
    this.ensureChannel()
  }

  public retry(fakeId: string): void {
    if (this.isHandedOff) {
      return
    }

    const failed = this.messages.find(
      message => message.state === 'failed' && message.fakeId === fakeId,
    )
    if (!failed || failed.state !== 'failed') {
      return
    }

    const sending: SendingMessage = {
      state: 'sending',
      fakeId: failed.fakeId,
      channelId: failed.channelId,
      authorId: failed.authorId,
      createdAt: failed.createdAt,
      text: failed.text,
      attachments: failed.attachments,
    }
    runInAction(() => {
      this.messages = this.messages.map(message =>
        message === failed ? sending : message,
      )
    })
    this.ensureChannel()
  }

  public complete(chat: Chat): void {
    runInAction(() => {
      this.materializedChat = chat
    })
  }

  private async ensureChannel(): Promise<void> {
    if (this.isCreating || this.isHandedOff) {
      return
    }
    this.isCreating = true

    const result = await this.api.createChat(
      '',
      false,
      this.userId,
      [this.userId, this.recipient.id],
      '',
    )
    this.isCreating = false

    fold(
      result,
      channelId => {
        this.isHandedOff = true
        this.onChannelCreated(channelId, this.messages)
      },
      error => {
        const reason = describeChatError(error)
        this.logger.warn(`create chat ${this.recipient.id}: ${reason}`)
        runInAction(() => {
          this.messages = this.messages.map(
            (message): DraftMessage =>
              message.state === 'sending'
                ? { ...message, state: 'failed', reason }
                : message,
          )
        })
      },
    )
  }
}
